import { useCompset } from "@/contexts/CompsetContext";
import { Dialog, DialogContent } from "../ui/dialog";
import { Button } from "@/components/ui/button";
import { usePlans } from "@/contexts/PlanRulesContext";
import { Building2, Trash2 } from "lucide-react";
import { useState } from "react";
import { apiErrorHandler } from "@/helpers/apiErrorHandler";
import { toast } from "sonner";
import ButtonLoader from "../ButtonLoader";
import { apiClient } from "@/services/apiClient";
import { API_URLS } from "@/constants/urls";
import { queryKeys } from "@/services/queryKeyFactory";
import { useQueryClient } from "@tanstack/react-query";

function DowngradeCompsetModal({ open, onSubmit }) {

    const [selected, setSelected] = useState({});

    const { maxCompetitorsPerCompset } = usePlans();
    const { compsets } = useCompset();
    const [loading, setLoading] = useState(false);

    const userCompsets = compsets?.user_compsets || [];
    const queryClient = useQueryClient();

    const toggleHotel = (compsetId, hotelId) => {
        setSelected(prev => {

            const current = prev[compsetId] || [];

            const updated = current.includes(hotelId)
                ? current.filter(id => id !== hotelId)
                : [...current, hotelId];

            return { ...prev, [compsetId]: updated };

        });
    };

    const isValid = userCompsets.every(cs => {

        const excess = Math.max(
            cs.competitors.length - maxCompetitorsPerCompset,
            0
        );

        return (selected[cs.compset_id]?.length || 0) >= excess;

    });


    const onRemoveCompetitors = async () => {

        try {

            setLoading(true)
            const payload = [];


            for (const cs of userCompsets) {

                const selectedIds = selected[cs.compset_id] || [];

                const remainingCompetitors = cs.competitors.filter(
                    c => selectedIds.includes(c.id)
                );


                if (remainingCompetitors.length < 1) {
                    toast.error(`${cs.compset_name} must have at least 1 competitor`);
                }

                if (remainingCompetitors.length > maxCompetitorsPerCompset) {
                    toast.error(`${cs.compset_name} exceeds competitor limit`);
                }

                payload.push({
                    comp_name: cs.compset_name,
                    comp_id: cs.compset_id,
                    competitors: remainingCompetitors
                });

            }

            const res = await apiClient.put(API_URLS.REMOVE_COMPSET, {
                compsets: payload
            })

            if (res.status === 200) {

                if (onSubmit) {
                    onSubmit();
                }

                await Promise.all(
                    [queryKeys.me(), queryKeys.compsets(), queryKeys.planRules()].map(key =>
                        queryClient.invalidateQueries({ queryKey: key })
                    )
                );

            }

        } catch (error) {

            apiErrorHandler(error);

        } finally {
            setLoading(false);
        }

    };

    return (
        <Dialog open={open} onOpenChange={() => { }}>
            <DialogContent
                className="max-w-2xl max-h-[90vh] flex flex-col [&>button]:hidden"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >

                <div className="py-4 flex flex-col flex-1 overflow-hidden">

                    {/* Header */}
                    <div className="text-center mb-6">

                        <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-3">
                            <Building2 className="w-6 h-6 text-primary" />
                        </div>

                        <h3 className="text-xl font-semibold">
                            Reduce Competitors to Continue
                        </h3>

                        <p className="text-sm text-muted-foreground mt-1">
                            Your new plan allows only {maxCompetitorsPerCompset} competitors per compset.
                            Please remove competitors before continuing.
                        </p>

                    </div>

                    {/* Scrollable List */}
                    <div className="space-y-6 flex-1 overflow-y-auto pr-2">

                        {userCompsets.map(cs => {

                            const excess =
                                cs.competitors.length - maxCompetitorsPerCompset;

                            if (excess <= 0) return null;

                            return (
                                <div
                                    key={cs.compset_id}
                                    className="border rounded-lg p-4"
                                >

                                    <div className="flex justify-between mb-3">

                                        <div className="font-medium">
                                            {cs.compset_name}
                                        </div>

                                        <span className="text-xs text-muted-foreground">
                                            Remove {excess} competitor{excess > 1 && "s"}
                                        </span>

                                    </div>

                                    <div className="space-y-2">

                                        {cs.competitors.map(hotel => {

                                            const selectedIds =
                                                selected[cs.compset_id] || [];

                                            const remaining =
                                                cs.competitors.length -
                                                selectedIds.length;

                                            const disableCheckbox =
                                                !selectedIds.includes(hotel.id) &&
                                                remaining <= 1;

                                            return (
                                                <label
                                                    key={hotel.id}
                                                    className={`flex items-center justify-between p-2 rounded cursor-pointer hover:bg-muted ${disableCheckbox
                                                        ? "opacity-50 cursor-not-allowed"
                                                        : ""
                                                        }`}
                                                >

                                                    <div className="flex items-center gap-3">

                                                        <input
                                                            type="checkbox"
                                                            disabled={disableCheckbox}
                                                            checked={
                                                                selectedIds.includes(
                                                                    hotel.id
                                                                ) || false
                                                            }
                                                            onChange={() =>
                                                                toggleHotel(
                                                                    cs.compset_id,
                                                                    hotel.id
                                                                )
                                                            }
                                                        />

                                                        <div>

                                                            <div className="text-sm font-medium">
                                                                {hotel.name}
                                                            </div>

                                                            <div className="text-xs text-muted-foreground">
                                                                ⭐ {hotel.rating} •{" "}
                                                                {hotel.reviews} reviews
                                                            </div>

                                                        </div>

                                                    </div>

                                                    <Trash2 className="w-4 h-4 text-muted-foreground" />

                                                </label>
                                            );
                                        })}

                                    </div>

                                </div>
                            );
                        })}

                    </div>

                    {/* Footer */}
                    <div className="flex justify-end mt-6 pt-4 border-t">

                        <Button
                            disabled={loading}
                            onClick={onRemoveCompetitors}
                        >
                            <ButtonLoader
                                loading={loading}
                                text={"Continue"}
                                loadingText="Validating..."
                            />
                        </Button>

                    </div>

                </div>

            </DialogContent>
        </Dialog>
    );
}

export default DowngradeCompsetModal;